import React, { MouseEventHandler, useContext, useMemo } from 'react'
import FileSaver from 'file-saver'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { getActiveServices } from 'src/pages/Services/services.helpers'
import { useContent } from 'src/context/hooks'
import {
  ListServicesRequest,
  ServiceRecord,
} from '@trustero/trustero-api-web/lib/service/service_pb'
import { useServices } from 'src/pages/Services/Services.hooks'
import { AuditBotPromiseClient } from '@trustero/trustero-api-web/lib/audit/auditbot_grpc_web_pb'
import {
  AuditReadinessTestRecord,
  GetAuditReadinessRequest,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { useInAudit } from 'src/context/AuditContext'
import { ConfirmationContext } from '../../../../../Confirmation'
import { SOC2Report } from '../../../../../Utils/reports/SOC2Report'
import { Soc2ReportModal } from '../Soc2ReportModal'
import { useSwrImmutableGrpc } from '../../../../../components/async/useSwrImmutableGrpc'
import { Spinner } from '../../../../../Throbber'
import { useAccount } from '../../../../../components/async/Account'
import { ControlWithTests } from '../../../../../Utils/reports/SOC2Report'

// auditId scopes the test results. The compliance dashboard leaves it unset and
// falls back to the audit context, which is empty there -- continuous-compliance
// scope. The audit index has no audit context at all, so it passes the selected
// audit's id explicitly; without it that export would silently fall back to
// continuous compliance rather than the audit being exported.
export const useDownloadSoc2Report = (
  auditId?: string,
): MouseEventHandler<HTMLButtonElement> => {
  const content = useContent()
  const { setConfirmationState } = useContext(ConfirmationContext)
  const req = new ListServicesRequest()
  const { data: serviceRes } = useServices(req)
  const {
    response: { data: controls },
  } = useSwrImmutableGrpc(
    ModelPromiseClient.prototype.listControls,
    new ListControlsRequest().setIsDismissed(new BoolValue().setValue(false)),
  )
  const {
    response: { data: frameworks },
  } = useSwrImmutableGrpc(
    ModelPromiseClient.prototype.listFrameworks,
    new Empty(),
  )
  // Latest control test results, from the same audit-readiness source the
  // controls index reads. An empty audit id is continuous-compliance scope,
  // matching ControlChecksProvider.
  const { auditId: contextAuditId } = useInAudit()
  const scopedAuditId = auditId || contextAuditId || ''
  const {
    response: { data: readiness },
  } = useSwrImmutableGrpc(
    AuditBotPromiseClient.prototype.getAuditReadiness,
    new GetAuditReadinessRequest()
      .setAuditId(scopedAuditId)
      .setCalculateStaleness(false),
  )
  const { data: accountRes } = useAccount()
  const account = accountRes?.toObject()
  const services = serviceRes
    ? serviceRes.getServicesList()
    : ([] as ServiceRecord[])
  const activeServices = getActiveServices(services, '')

  const entity = account?.orgname !== '' ? account?.orgname : 'Entity'

  const data: ControlWithTests[] = useMemo(() => {
    // Readiness keys on control_models.id; index by the human model id too,
    // the way ControlChecksProvider does, so either resolves.
    const testsByControl: Record<string, AuditReadinessTestRecord[]> = {}
    readiness?.getAuditReadinessList().forEach((record) => {
      const tests = record.getControlTestsList()
      testsByControl[record.getControlId()] = tests
      testsByControl[record.getControlModelId()] = tests
    })

    return (
      controls?.toObject().itemsList.map((control) => ({
        control,
        tests:
          testsByControl[control.id] ?? testsByControl[control.modelId] ?? [],
      })) ?? []
    )
  }, [controls, readiness])

  const body = data.length ? (
    <Soc2ReportModal />
  ) : (
    <Spinner color="primary" size="xxl" mt="xl" />
  )

  return () => {
    setConfirmationState({
      isShown: true,
      size: 'lg',
      title: 'Export PDF Pro Forma SOC 2 Report',
      body,
      confirmText: 'Export PDF',
      onConfirmCB: async () => {
        if (!frameworks || !entity || !data) {
          return
        }
        const report = new SOC2Report()
        const pdf = await report.report(
          entity,
          content,
          activeServices,
          frameworks.toObject().itemsList,
          data,
        )
        return FileSaver.saveAs(pdf, 'Pro-Forma-SOC2.pdf')
      },
    })
  }
}
